console.log('[Yukon] Game.js module loading...')

import './patches/patches'

import Boot from '@engine/boot/Boot'
import InterfaceController from '@engine/interface/InterfaceController'
import MemoryManager from '@engine/memory/MemoryManager'
import RuffleController from '@engine/ruffle/RuffleController'
import WorldController from '@engine/world/WorldController'

import Network from '@engine/network/Network'
import registerNinePatchContainerFactory from '@engine/utils/ninepatch/registerNinePatchContainerFactory'
import GA4Manager from '@engine/utils/GA4Manager'
import MixpanelClientService from '@engine/utils/MixpanelClientService'
import SentryClient from '@engine/utils/SentryClient'
import GDPRManager from '@engine/utils/GDPRManager'
import configLoader from '@engine/utils/ConfigLoader'

let soundManagerModulePromise = null

function loadSoundManagerModule() {
    if (!soundManagerModulePromise) {
        soundManagerModulePromise = import(/* webpackChunkName: "engine-sound-manager" */ '@engine/sound/SoundManager')
            .then(module => module.default || module)
            .catch((error) => {
                soundManagerModulePromise = null
                throw error
            })
    }

    return soundManagerModulePromise
}

class SilentSoundManager {
    constructor() {
        this.muteMusic = false
        this.muteBGS = false
        this.muteSFX = false
    }

    play() { return null }
    playMusic() {}
    stopAll() {}
    stopAllButMusic() {}
    stopMusic() {}
    add() { return null }
    remove() {}
}

import game from './data/game'
import './styles/game.css'


export default class Game extends Phaser.Game {

    constructor(config) {
        super(config)

        this.logBanner()

        // Initialize GDPR Manager first
        this.gdprManager = GDPRManager.getInstance()

        // Sentry is already initialized in initializeApp, don't initialize again
        // Just set up global error handlers if not already done
        if (!window.sentryInitialized) {
            SentryClient.setupGlobalErrorHandlers()
        }

        // Removes focus from active element
        this.canvas.addEventListener('click', () => document.activeElement.blur())

        this.crumbs = config.crumbs
        this.network = new Network(this)
        
        // Initialize WebAPI client (required for authentication)
        if (!config.webapi || !config.webapi.enabled) {
            console.error('[Game] WebAPI configuration is required but not found!')
            alert('WebAPI not configured. Please check config.json\n\nReloading page...')
            setTimeout(() => window.location.reload(), 2000)
            throw new Error('WebAPI configuration is required')
        }
        
        try {
            this.network.initWebAPI(config.webapi)
        } catch (error) {
            console.error('[Game] Failed to initialize WebAPI:', error)
            alert('Failed to connect to WebAPI server.\n\nPlease ensure the WebAPI service is running.\n\nReloading page...')
            setTimeout(() => window.location.reload(), 3000)
            throw error
        }

        // howler.js based sound manager
    this.soundManager = new SilentSoundManager()
    this.initializeSoundManager()

        // Initialize analytics with GDPR compliance
        this.initializeAnalytics(config)

        registerNinePatchContainerFactory()

        // Add initial scenes
        this.scene.add('InterfaceController', InterfaceController)
        this.scene.add('MemoryManager', MemoryManager)
        this.scene.add('RuffleController', RuffleController)
        this.scene.add('WorldController', WorldController)

        // Start boot
        this.scene.add('Boot', Boot, true)

        // Listen for GDPR consent changes
        window.addEventListener('gdprConsentChanged', (event) => {
            this.onGDPRConsentChanged(event.detail)
        })

        // Track game initialization (only if consent given)
        if (this.gdprManager.canCollectCrashReports()) {
            SentryClient.trackGameEvent('game_initialized', {
                version: VERSION || 'unknown',
                config: {
                    hasMixpanel: !!config.mixpanel,
                    hasCrumbs: !!config.crumbs
                }
            })
        }
    }

    initializeAnalytics(config) {
        // Initialize GA4 Analytics
        this.analytics = GA4Manager
        
        // Initialize GA4 now that config is available
        this.analytics.init()

        // Initialize Mixpanel Analytics
        this.mixpanel = new MixpanelClientService()
        
        // Initialize Mixpanel with config from game data
        if (config.mixpanel) {
            window.mixpanelConfig = config.mixpanel
            this.mixpanel.initialize(config.mixpanel)
        }
        
        // Make services globally available
        window.mixpanel = this.mixpanel
        window.sentry = SentryClient
        window.gdprManager = this.gdprManager
    }

    async initializeSoundManager() {
        try {
            const SoundManager = await loadSoundManagerModule()
            const soundManager = new SoundManager(this)
            // Settings are now loaded from localStorage in the SoundManager constructor
            this.soundManager = soundManager
            this.updateSceneSoundManagers(soundManager)
        } catch (error) {
            console.error('[Game] Failed to load sound manager', error)
        }
    }

    updateSceneSoundManagers(soundManager) {
        if (!this.scene) {
            return
        }

        const activeScenes = this.scene.getScenes(false) || []

        activeScenes.forEach((scene) => {
            if (scene && typeof scene === 'object') {
                scene.soundManager = soundManager
            }
        })
    }

    onGDPRConsentChanged(consentData) {
        console.log('[Game] GDPR consent changed:', consentData)
        
        // Update analytics services based on new consent
        if (consentData.preferences.analytics) {
            this.analytics.enable()
            this.mixpanel.enable()
        } else {
            this.analytics.disable()
            this.mixpanel.disable()
        }

        // Update crash reporting
        if (consentData.preferences.crashReporting) {
            if (typeof SentryClient.enable === 'function') {
                SentryClient.enable()
            }
        } else {
            if (typeof SentryClient.disable === 'function') {
                SentryClient.disable()
            }
        }
    }

    logBanner() {
        // Banner removed for production
    }

}

/**
 * Initialize the application with configuration loading
 */
async function initializeApp() {
    console.log('[Yukon] initializeApp() called')
    try {
        // Load configuration first
        console.log('[Yukon] Loading config...')
        await configLoader.loadConfig()
        const loadedConfig = configLoader.config
        console.log('[Yukon] Config loaded:', loadedConfig)
        
        // Initialize Sentry with the loaded configuration
        if (loadedConfig.analytics) {
            await SentryClient.initialize(loadedConfig)
            SentryClient.setupGlobalErrorHandlers()
            window.sentryInitialized = true
        }
        
        // Merge the loaded config with the game config
        const gameConfig = {
            ...game,
            ...loadedConfig
        }
        
        console.log('[Yukon] Creating Game instance...')
        // Now start the game with merged config
        new Game(gameConfig)
        console.log('[Yukon] Game instance created')
        
    } catch (error) {
        console.error('[Yukon] Failed to initialize application:', error)
        
        // Log initialization error to Sentry if available
        if (window.sentry) {
            window.sentry.captureException(error, {
                operation: 'app_initialization',
                stage: 'config_loading'
            })
        }
        
        // Fallback: try to start game anyway with default config
        try {
            console.log('[Yukon] Attempting fallback with default config...')
            new Game(game)
        } catch (fallbackError) {
            console.error('[Yukon] Failed to start game with default configuration:', fallbackError)
            
            if (window.sentry) {
                window.sentry.captureException(fallbackError, {
                    operation: 'app_initialization',
                    stage: 'fallback_start'
                })
            }
        }
    }
}

window.onload = () => {
    console.log('[Yukon] window.onload fired')
    initializeApp()
}
